// Trace-row anchor ID helpers per plan §7.8.8 (run-trace permalink + share).
//
// Anchor formats (preserved through reloads via window.location.hash):
//   sub-call/<sub_prompt_id>/<item_key>/<attempt>
//   bridge-step/<step_id>
//
// item_key may be empty string for non-fan-out sub-calls; we render "_" so
// the segment count is stable for parsing. Attempt defaults to 1 (the
// first attempt's row anchor); the diff tab handles attempt 2 within the
// same expanded panel.

import type { SubCallTrace, BridgeStepTrace } from "./traceApi";

const ITEM_KEY_PLACEHOLDER = "_";

export function subCallAnchorId(sc: SubCallTrace, attempt = 1): string {
  const itemKey = sc.item_key === "" ? ITEM_KEY_PLACEHOLDER : sc.item_key;
  return `sub-call/${sc.sub_prompt_id}/${itemKey}/attempt_${attempt}`;
}

export function bridgeStepAnchorId(step: BridgeStepTrace): string {
  return `bridge-step/${step.step_id}`;
}

// parseAnchor returns the kind of trace element a hash points to, or null
// if the hash doesn't match a known format. Used by RunTrace.tsx on mount
// to decide whether to scroll/expand a row after the trace loads.
export interface ParsedAnchor {
  kind: "sub-call" | "bridge-step";
  raw: string; // exact element id to look up via document.getElementById
}

export function parseAnchor(hash: string): ParsedAnchor | null {
  // Hash may arrive as "#sub-call/..." or already stripped; tolerate both.
  const stripped = hash.startsWith("#") ? hash.slice(1) : hash;
  if (stripped === "") return null;
  if (stripped.startsWith("sub-call/")) {
    return { kind: "sub-call", raw: stripped };
  }
  if (stripped.startsWith("bridge-step/")) {
    return { kind: "bridge-step", raw: stripped };
  }
  return null;
}

// TRACE_ANCHOR_FOCUS_EVENT — bus event for cross-component "expand me"
// signaling. Emitted by:
//   - RunTrace.tsx on mount when window.location.hash matches a row
//   - RunOutliers.tsx when a user clicks an outlier
// Listened to by SubCallRow + BridgeStepRow to auto-expand themselves when
// their anchor id matches.
export const TRACE_ANCHOR_FOCUS_EVENT = "trace-anchor-focus";
